import type { Schemas } from '@npc/api-client'

export type PlatformEvent = Schemas['Event']

const FAST_DELIVERY_EVENT_TYPES = new Set<PlatformEvent['type']>([
  'job.progress',
  'gate.rejected',
  'build.failed',
  'deployed',
  'ready_for_test',
  'runtime.error',
  'rolled_back',
  'release.updated',
])

export function isFastDeliveryRelevantEvent(type: PlatformEvent['type']): boolean {
  return FAST_DELIVERY_EVENT_TYPES.has(type)
}

/**
 * 会话有新消息时，哪些 query 要失效。
 *
 * ------------------------------------------------------------------
 * 2026-09-08 arron：「这里应该有新消息推送，新消息来了右边对话框应该会
 * 自动到最新消息，现在对话空停了不动」。
 *
 * 在这之前消息**不走任何推送**：会话那根 SSE 只推执行过程，消息本身靠
 * 5 秒轮询，切到后台还会停。这根全站流平时收到事件只推进游标——
 * L-67 把会话列表加进 LIVE_LEDGER_ROOT_KEYS 时以为「列表接上 socket 了」，
 * 其实那份名单只在断线重连时刷一次。
 *
 * 现在服务端在消息落库后发 conversation.message（L-73），这里把它翻成
 * 四把 key：两份侧栏列表（打未读点要用 updated_at），以及**那一条会话**
 * 的消息与会话行。别的会话不碰：一条消息不该让整站每个面板都去问一遍。
 * ------------------------------------------------------------------
 */
export function queryKeysStaledByConversationMessage(
  event: Pick<PlatformEvent, 'type' | 'payload'>,
): readonly (readonly string[])[] {
  if (event.type !== 'conversation.message') return []
  const raw = (event.payload as { conversation_id?: unknown } | undefined)?.conversation_id
  const id = typeof raw === 'string' && raw !== '' ? raw : null
  const keys: (readonly string[])[] = [['lulu-conversations'], ['lulu-project-conversations']]
  if (id !== null) keys.push(['lulu-messages', id], ['lulu-conversation', id])
  return keys
}

export const STATE_LANE = ['draft', 'running', 'gating', 'reviewing', 'ci_running', 'ready'] as const

export type TaskStep = { index: number; total: number }

export type RunningJob = {
  jobId: string
  projectId: string
  message: string
  copyTemplate: string
}

export type TaskLive = {
  message: string
  copyTemplate: string
  step?: TaskStep
  actor?: string
  state?: string
}

const TERMINAL_COPY = new Set(['job.succeeded', 'job.failed', 'job.canceled'])
const ACTIVE_COPY = new Set(['job.started', 'job.step.done', 'job.resumed', 'job.stopped.too_long', 'job.stopped.too_many_steps', 'job.stopped.budget_used_up', 'job.stopped.no_progress', 'job.step.not_started'])
const RUNNING_STATES = new Set(['running', 'gating', 'pending_approval', 'reviewing', 'ci_running', 'v2_handoff', 'fix_requested'])

function payloadString(payload: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = payload?.[key]
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

function payloadStep(payload: Record<string, unknown> | undefined): TaskStep | undefined {
  const index = Number(payload?.index)
  const total = Number(payload?.total)
  if (!Number.isFinite(index) || !Number.isFinite(total) || total <= 0) return undefined
  return { index, total }
}

function jobIdOf(event: PlatformEvent): string | undefined {
  if (event.job_id) return event.job_id
  return payloadString(event.payload as Record<string, unknown> | undefined, 'job_id')
}

function taskIdOf(event: PlatformEvent): string | undefined {
  return payloadString(event.payload as Record<string, unknown> | undefined, 'task_id')
}

function actorOf(event: PlatformEvent): string | undefined {
  const payload = event.payload as Record<string, unknown> | undefined
  return payloadString(payload, 'actor') ?? payloadString(payload, 'executor')
}

/** 进度：有步数用步数，否则走 state 车道。不编百分数文案。 */
export function taskProgress(state: string, step?: TaskStep | null): { ratio: number; paused: boolean } {
  if (step && step.total > 0) {
    return { ratio: Math.min(1, Math.max(0, step.index / step.total)), paused: false }
  }
  const paused = state === 'fix_requested' || state === 'pending_approval'
  let lane = STATE_LANE.indexOf(state as (typeof STATE_LANE)[number])
  if (state === 'preparing' || state === 'ready_to_claim') lane = 0
  if (state === 'v2_handoff') lane = STATE_LANE.indexOf('ci_running')
  if (paused) {
    lane = state === 'pending_approval' ? STATE_LANE.indexOf('gating') : STATE_LANE.indexOf('reviewing')
  }
  if (lane < 0) lane = 0
  return { ratio: (lane + 1) / STATE_LANE.length, paused }
}

export function reduceLiveEvents(events: PlatformEvent[]): {
  runningByProject: Map<string, RunningJob[]>
  latestByTask: Map<string, TaskLive>
} {
  const jobs = new Map<string, RunningJob & { running: boolean }>()
  const latestByTask = new Map<string, TaskLive>()

  for (const event of events) {
    if (event.type !== 'job.progress') continue
    const copy = event.copy_template ?? ''
    const jobId = jobIdOf(event)
    const projectId = event.project_id ?? undefined
    const taskId = taskIdOf(event)
    const message = event.message ?? ''
    const step = payloadStep(event.payload as Record<string, unknown> | undefined)
    const actor = actorOf(event)

    if (taskId) {
      const prev = latestByTask.get(taskId)
      const nextState = payloadString(event.payload as Record<string, unknown> | undefined, 'state') ?? prev?.state
      latestByTask.set(taskId, {
        message: message || prev?.message || '',
        copyTemplate: copy || prev?.copyTemplate || '',
        step: step ?? prev?.step,
        actor: actor ?? prev?.actor,
        state: nextState,
      })
    }

    if (!jobId || !projectId) continue
    const state = payloadString(event.payload as Record<string, unknown> | undefined, 'state')
    if (TERMINAL_COPY.has(copy) || (state !== undefined && (state === 'ready' || state === 'failed' || state === 'cancelled'))) {
      jobs.delete(jobId)
      continue
    }
    if (!ACTIVE_COPY.has(copy) && !copy.startsWith('job.stopped') && !(state !== undefined && RUNNING_STATES.has(state))) continue
    jobs.set(jobId, {
      jobId,
      projectId,
      message,
      copyTemplate: copy,
      running: true,
    })
  }

  const runningByProject = new Map<string, RunningJob[]>()
  for (const job of jobs.values()) {
    if (!job.running) continue
    const list = runningByProject.get(job.projectId) ?? []
    list.push(job)
    runningByProject.set(job.projectId, list)
  }
  return { runningByProject, latestByTask }
}

export function eventsForProject(events: PlatformEvent[], projectId: string): PlatformEvent[] {
  return events.filter((event) => event.project_id === projectId)
}

const CODE_PENDING_STATES = new Set(['forwarded', 'held', 'pending_approval'])

/**
 * 把 `GET /events` 里守门 / 代推那一类叠进「代码与发布」已有的 REST 底图。
 *
 * `deployed` 带着灰度比例也只是部署证据，**不许**据此画出「已上线」「全量」。
 * `forwarded` 只表示进了 Forgejo，同样不是上线。
 */
export function applyCodeLiveEvents(
  base: Schemas['PendingChanges'] | undefined,
  events: PlatformEvent[],
): Schemas['PendingChanges'] | undefined {
  if (!base) return base

  let pending = [...(base.pending ?? [])]
  let rejected = [...(base.rejected ?? [])]

  for (const event of events) {
    const payload = event.payload as Record<string, unknown> | undefined
    if (event.type === 'deployed') continue

    if (event.type === 'gate.rejected') {
      const commit = payloadString(payload, 'commit')
      const branch = payloadString(payload, 'branch')
      const repo = payloadString(payload, 'repo')
      if (!commit || !branch || !repo) continue
      const reason = event.message ?? ''
      rejected = [
        {
          repo,
          branch,
          commit,
          reason,
          plain: reason,
          detail: payloadString(payload, 'detail'),
          gate: payloadString(payload, 'gate') as Schemas['RejectedChange']['gate'],
          at: event.at,
        },
        ...rejected.filter((row) => row.commit !== commit),
      ]
      pending = pending.filter((row) => row.commit !== commit)
      continue
    }

    if (event.type !== 'job.progress') continue
    const commit = payloadString(payload, 'commit')
    const branch = payloadString(payload, 'branch')
    const repo = payloadString(payload, 'repo')
    const nextState = payloadString(payload, 'state')
    if (!commit || !branch || !repo || !nextState || !CODE_PENDING_STATES.has(nextState)) continue
    const state = nextState as Schemas['PendingChange']['state']
    const existing = pending.find((row) => row.commit === commit)
    const next: Schemas['PendingChange'] = {
      ...(existing ?? { repo, branch, commit, at: event.at }),
      repo,
      branch,
      commit,
      state,
      what: event.message || existing?.what,
      at: event.at,
    }
    pending = [next, ...pending.filter((row) => row.commit !== commit)]
  }

  return { ...base, pending, rejected }
}

/**
 * 任务有动静（领到了 / 推上去了 / 上线了 / 红线 …）时，哪些 query 要失效。
 *
 * 服务端早就往全站流发 development_task.* 事件，2026-09-09 之前浏览器收到
 * 只推进游标——与 conversation.message 那个洞同一个形状。类型枚举里没有这些
 * 值（服务端发得比契约多），所以按前缀认，不按枚举认。
 */
export function queryKeysStaledByTaskEvent(
  event: Pick<PlatformEvent, 'type' | 'payload' | 'project_id'>,
): readonly (readonly string[])[] {
  const type = String(event.type)
  if (!type.startsWith('development_task.') && type !== 'task.completed') return []
  const keys: (readonly string[])[] = [
    ['development-tasks'],
    ['lulu-decisions'],
    ['lulu-workboard'],
    ['lulu-plan'],
  ]
  if (typeof event.project_id === 'string' && event.project_id !== '') {
    keys.push(['projects', event.project_id, 'development-tasks'])
  }
  return keys
}
